import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Check, Upload, Rss, PenLine, Database, Crosshair, Truck, DollarSign, Shield, BookOpen, HeartPulse, Layers } from "lucide-react";
import { TopBar } from "@/components/dravian/top-bar";
import { FooterStrip } from "@/components/dravian/footer-strip";
import { useWorkspaces } from "@/store/workspaces";
import { ontologies as ontologiesApi, type ApiOntology } from "@/lib/api";
import type { WorkspaceDomain } from "@/data/mock";

export const Route = createFileRoute("/workspaces/new")({
  head: () => ({ meta: [{ title: "New Workspace — Dravian Horizon" }] }),
  component: NewWorkspace,
});

const DOMAINS: { id: WorkspaceDomain; label: string; icon: typeof Crosshair; desc: string }[] = [
  { id: "geopolitical", label: "Geopolitical", icon: Crosshair, desc: "Conflict, territory, state and non-state actors." },
  { id: "supply_chain", label: "Supply Chain", icon: Truck, desc: "Suppliers, logistics, manufacturing dependencies." },
  { id: "financial", label: "Financial", icon: DollarSign, desc: "Counterparty, credit, market exposure." },
  { id: "threat", label: "Threat", icon: Shield, desc: "Adversaries, infrastructure, indicators." },
  { id: "compliance", label: "Compliance", icon: BookOpen, desc: "Regulatory, sanctions, internal controls." },
  { id: "healthcare", label: "Healthcare", icon: HeartPulse, desc: "Providers, cohorts, medications, devices, outbreaks." },
  { id: "custom", label: "Custom", icon: Layers, desc: "Define your own domain and ontology." },
];

// Friendly copy for shipped ontology names (matches the `name:` field in the
// YAML files under backend/ontologies/).
const ONTOLOGY_META: Record<string, { label: string; desc: string }> = {
  colombian_conflict: {
    label: "Conflict",
    desc: "Armed groups, municipalities, incidents, displacement.",
  },
  financial: {
    label: "Financial",
    desc: "Companies, counterparties, instruments, ratings, sanctions.",
  },
  supply_chain: {
    label: "Supply Chain",
    desc: "Suppliers, manufacturers, parts, lanes, disruptions.",
  },
  healthcare: {
    label: "Healthcare",
    desc: "Providers, clinicians, cohorts, medications, devices, outbreaks.",
  },
};

const BLANK_ONTOLOGY = {
  label: "Blank",
  desc: "Start empty; types get inferred from your data as you ingest.",
};

// Which ontology to pre-pick based on the chosen domain. Falls back to blank.
const DOMAIN_TO_ONTOLOGY: Partial<Record<WorkspaceDomain, string>> = {
  geopolitical: "colombian_conflict",
  financial: "financial",
  supply_chain: "supply_chain",
  healthcare: "healthcare",
};

const SOURCES = [
  { id: "upload", icon: Upload, label: "Upload CSV / JSON", desc: "Drop a file with entities or signals." },
  { id: "rss", icon: Rss, label: "Connect RSS feed", desc: "Ingest news from public RSS sources." },
  { id: "manual", icon: PenLine, label: "Start empty", desc: "Create entities by hand later." },
  { id: "api", icon: Database, label: "Connect API", desc: "Pull from your internal data API." },
];

function NewWorkspace() {
  const navigate = useNavigate();
  const { createWorkspace } = useWorkspaces();
  const ontologiesQ = useQuery({
    queryKey: ["ontologies"],
    queryFn: ontologiesApi.list,
  });
  const [step, setStep] = useState(0);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [domain, setDomain] = useState<WorkspaceDomain>("geopolitical");
  // null = blank (no ontology assigned)
  const [ontologyId, setOntologyId] = useState<string | null>(null);
  const [ontologyTouched, setOntologyTouched] = useState(false);
  const [source, setSource] = useState("manual");

  // Auto-suggest an ontology when the user changes domain (until they pick manually)
  const ontologies = ontologiesQ.data ?? [];
  const suggestedId = useMemo(() => {
    const wanted = DOMAIN_TO_ONTOLOGY[domain];
    if (!wanted) return null;
    return ontologies.find((o) => o.name === wanted)?.id ?? null;
  }, [domain, ontologies]);

  // Auto-pick the suggested ontology as long as the user hasn't picked one manually.
  useEffect(() => {
    if (!ontologyTouched) setOntologyId(suggestedId);
  }, [suggestedId, ontologyTouched]);

  const next = () => setStep((s) => Math.min(2, s + 1));
  const prev = () => setStep((s) => Math.max(0, s - 1));
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const create = async () => {
    setCreateError(null);
    setCreating(true);
    try {
      const ws = await createWorkspace({
        name: name || "Untitled Workspace",
        description,
        domain,
        ontology_id: ontologyId,
      });
      navigate({ to: "/$workspace/geographic", params: { workspace: ws.id } });
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : "Failed to create workspace");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <TopBar />
      <main className="flex-1 mx-auto w-full max-w-[820px] px-6 py-10">
        <Link to="/hq" className="text-[12px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 mb-6"><ChevronLeft className="h-3 w-3" /> Back to Dashboard</Link>
        <div className="label-eyebrow mb-1">New Workspace</div>
        <h1 className="text-[28px] font-semibold tracking-tight">Create a workspace</h1>
        <p className="text-[13px] text-muted-foreground mt-1">Workspaces hold entities, signals, scenarios, and the ontology that links them.</p>

        <div className="flex items-center gap-2 my-8">
          {["Details", "Ontology", "Data Sources"].map((label, i) => (
            <div key={i} className="flex items-center gap-2 flex-1">
              <div className={`grid h-7 w-7 place-items-center rounded-full text-[11px] font-semibold border ${i <= step ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground"}`}>
                {i < step ? <Check className="h-3.5 w-3.5" /> : i + 1}
              </div>
              <div className={`text-[12px] ${i === step ? "text-foreground" : "text-muted-foreground"}`}>{label}</div>
              {i < 2 && <div className={`flex-1 h-px ${i < step ? "bg-primary" : "bg-border"}`} />}
            </div>
          ))}
        </div>

        <div className="panel p-6 min-h-[360px]">
          {step === 0 && (
            <div className="space-y-4">
              <Field label="Name">
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Colombia Conflict Map" className="w-full bg-surface-elevated border border-border rounded-md px-3 py-2.5 text-[13px] outline-none focus:border-primary/50" />
              </Field>
              <Field label="Description">
                <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} placeholder="What will this workspace track?" className="w-full bg-surface-elevated border border-border rounded-md px-3 py-2.5 text-[13px] outline-none focus:border-primary/50 resize-none" />
              </Field>
              <Field label="Domain">
                <div className="grid grid-cols-3 gap-2">
                  {DOMAINS.map((d) => {
                    const Icon = d.icon;
                    const active = domain === d.id;
                    return (
                      <button key={d.id} onClick={() => setDomain(d.id)} className={`text-left rounded-md border px-3 py-2.5 transition ${active ? "border-primary/50 bg-primary/10" : "border-border hover:border-primary/30 bg-surface-elevated/40"}`}>
                        <Icon className={`h-4 w-4 mb-1.5 ${active ? "text-primary" : "text-muted-foreground"}`} />
                        <div className="text-[12px] font-medium">{d.label}</div>
                        <div className="text-[10px] text-muted-foreground leading-snug mt-0.5">{d.desc}</div>
                      </button>
                    );
                  })}
                </div>
              </Field>
            </div>
          )}
          {step === 1 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="label-eyebrow">Choose an ontology</div>
                {suggestedId && ontologyId === suggestedId && (
                  <span className="text-[10px] text-primary">Suggested for {DOMAINS.find((d) => d.id === domain)?.label}</span>
                )}
              </div>
              {ontologiesQ.isLoading ? (
                <div className="text-[12px] text-muted-foreground">Loading…</div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  {ontologies.map((o) => {
                    const meta = ONTOLOGY_META[o.name] ?? {
                      label: o.name.replace(/_/g, " "),
                      desc: `${o.entity_types.length} entity types · ${o.relationship_types.length} relationships · ${o.signal_types.length} signals.`,
                    };
                    const active = ontologyId === o.id;
                    return (
                      <button
                        key={o.id}
                        onClick={() => {
                          setOntologyTouched(true);
                          setOntologyId(o.id);
                        }}
                        className={`text-left rounded-md border p-4 ${active ? "border-primary/50 bg-primary/10" : "border-border hover:border-primary/30 bg-surface-elevated/40"}`}
                      >
                        <div className="text-[14px] font-semibold capitalize">{meta.label}</div>
                        <div className="text-[12px] text-muted-foreground mt-1">{meta.desc}</div>
                        <div className="text-[10px] text-muted-foreground/70 font-mono mt-2">
                          {o.entity_types.length}E · {o.relationship_types.length}R · {o.signal_types.length}S · v{o.version}
                        </div>
                      </button>
                    );
                  })}
                  <button
                    onClick={() => {
                      setOntologyTouched(true);
                      setOntologyId(null);
                    }}
                    className={`text-left rounded-md border p-4 ${ontologyId === null ? "border-primary/50 bg-primary/10" : "border-border hover:border-primary/30 bg-surface-elevated/40"}`}
                  >
                    <div className="text-[14px] font-semibold">{BLANK_ONTOLOGY.label}</div>
                    <div className="text-[12px] text-muted-foreground mt-1">{BLANK_ONTOLOGY.desc}</div>
                  </button>
                </div>
              )}
            </div>
          )}
          {step === 2 && (
            <div className="space-y-3">
              <div className="label-eyebrow">Initial data source</div>
              <div className="grid grid-cols-2 gap-3">
                {SOURCES.map((s) => {
                  const Icon = s.icon;
                  const active = source === s.id;
                  return (
                    <button key={s.id} onClick={() => setSource(s.id)} className={`text-left rounded-md border p-4 flex gap-3 ${active ? "border-primary/50 bg-primary/10" : "border-border hover:border-primary/30 bg-surface-elevated/40"}`}>
                      <Icon className={`h-5 w-5 mt-0.5 ${active ? "text-primary" : "text-muted-foreground"}`} />
                      <div>
                        <div className="text-[13px] font-semibold">{s.label}</div>
                        <div className="text-[11px] text-muted-foreground mt-1">{s.desc}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between mt-5">
          <button onClick={prev} disabled={step === 0} className="inline-flex items-center gap-1.5 text-[13px] text-muted-foreground hover:text-foreground disabled:opacity-30">
            <ChevronLeft className="h-4 w-4" /> Back
          </button>
          {step < 2 ? (
            <button onClick={next} className="inline-flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground px-4 py-2 text-[13px] font-medium">
              Continue <ChevronRight className="h-4 w-4" />
            </button>
          ) : (
            <div className="flex items-center gap-3">
              {createError && (
                <span className="text-[12px] text-risk-high">{createError}</span>
              )}
              <button
                onClick={create}
                disabled={creating}
                className="inline-flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground px-4 py-2 text-[13px] font-medium glow-primary disabled:opacity-60"
              >
                {creating ? "Creating…" : (<>Create Workspace <Check className="h-4 w-4" /></>)}
              </button>
            </div>
          )}
        </div>
      </main>
      <FooterStrip />
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="label-eyebrow mb-1.5">{label}</div>
      {children}
    </div>
  );
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
type _UnusedApiOntology = ApiOntology;
